import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header as DsHeader, defaultNavItems } from "@/design-system";
import { listRecentDecksFromLocalStorage, subscribeToDeckStorageUpdates } from "@/lib/deck-storage";
import { localCardImage } from "@/lib/card-images";
import type { DeckView } from "@/lib/types";
import { cn } from "@/lib/utils";
import { Logo } from "./Logo";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

export function Header() {
  const items = defaultNavItems
    .filter((item) => item.to !== "/deck")
    .map((item) => ({
      ...item,
      labelVisibleFrom:
        item.to === "/"
          ? ("sm" as const)
          : item.to === "/import"
            ? ("md" as const)
            : ("lg" as const),
      visibleFrom: item.to === "/" ? ("sm" as const) : undefined,
    }));

  return (
    <DsHeader
      className="z-50"
      items={items}
      brand={
        <motion.span
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="flex"
        >
          <Logo
            size={56}
            markOffsetY={12}
            textOffsetY={5}
            inlineSlogan
            wordmarkClassName="hidden whitespace-nowrap text-lg sm:inline-block sm:text-2xl"
            sloganClassName="brand-slogan-silver hidden whitespace-nowrap text-[0.8rem] sm:inline-block sm:text-sm"
          />
        </motion.span>
      }
      actions={
        <div className="flex items-center gap-3">
          <RecentDecksNav />
          <EngineBadge />
        </div>
      }
    />
  );
}

function RecentDecksNav() {
  const [decks, setDecks] = useState<DeckView[]>(() =>
    listRecentDecksFromLocalStorage().slice(0, 3),
  );

  useEffect(() => {
    const refresh = () => setDecks(listRecentDecksFromLocalStorage().slice(0, 3));
    refresh();
    return subscribeToDeckStorageUpdates(refresh);
  }, []);

  return (
    <nav
      aria-label="Last generations"
      className="flex h-11 items-center gap-2 rounded-[var(--radius-lg)] border border-ds-gold-primary/25 bg-ds-black-secondary/55 px-2 backdrop-blur-md sm:gap-2.5 sm:px-2.5"
    >
      <span className="whitespace-nowrap font-heading text-[9px] font-semibold uppercase leading-tight tracking-[0.12em] text-ds-gold-accent sm:text-[10px]">
        <span className="lg:hidden">Last 3</span>
        <span className="hidden lg:inline">Last generations</span>
      </span>
      {decks.length > 0 ? (
        <span className="flex items-center gap-1" aria-label={`${decks.length} recent decks`}>
          {decks.map((deck, index) => {
            const commanderNames = deck.commanders.map((commander) => commander.name).join(" & ");
            const commanderArt = deck.commanders[0]?.art;
            const image = localCardImage(commanderArt?.imageSmall ?? commanderArt?.imageNormal);
            return (
              <Link
                key={deck.result.deckSeed}
                to={`/d/${deck.result.deckSeed}`}
                aria-label={`Open recent generation ${index + 1}: ${commanderNames}`}
                title={commanderNames}
                className="group/recent-deck relative flex h-9 w-6 items-center justify-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ds-gold-primary"
              >
                <motion.span
                  whileHover={{ scale: 1.75, y: 3, zIndex: 30 }}
                  transition={{ type: "spring", stiffness: 360, damping: 22 }}
                  className="relative block aspect-[63/88] w-5 overflow-hidden rounded-[3px] border border-ds-gold-primary/40 bg-ds-black-secondary shadow-md transition-transform group-focus-visible/recent-deck:scale-150"
                >
                  <img
                    src={image ?? "/cards/card-back.svg"}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                    className="h-full w-full object-cover"
                  />
                </motion.span>
              </Link>
            );
          })}
        </span>
      ) : (
        <Link
          to="/deck"
          className="whitespace-nowrap rounded-[var(--radius-md)] px-1 py-1 text-[10px] text-ds-text-muted transition-colors hover:text-ds-gold-light"
          aria-label="No recent generations yet. Open decks."
        >
          —
        </Link>
      )}
    </nav>
  );
}

function EngineBadge() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className={cn(
            "ml-2 hidden items-center gap-1.5 rounded-full border px-2.5 py-1 font-heading text-[length:var(--font-size-xs)] font-semibold tracking-wide focus-visible:outline-none focus-visible:shadow-[var(--focus-ring)] lg:inline-flex",
            "border-ds-success/40 text-ds-success",
          )}
        >
          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-ds-success" />
          Engine
        </span>
      </TooltipTrigger>
      <TooltipContent>Builds run on the local 99 Problems deck engine service.</TooltipContent>
    </Tooltip>
  );
}
